'use client';

import { useEffect } from 'react';

/**
 * Site-wide scroll storytelling engine (CSS + IntersectionObserver).
 *
 * Elements opt in with the `reveal` class (plus optional modifiers:
 * `reveal-left`, `reveal-right`, `reveal-scale`). Children of a
 * `reveal-stagger` container animate in sequence. Stat counters opt in
 * with `data-count` on the element containing the number text.
 *
 * Deliberately NOT GSAP: tweened nodes died when React regenerated the
 * tree after the locale hydration mismatch (elements stuck at opacity 0).
 * CSS transitions + a MutationObserver survive re-renders and cost ~0 JS
 * on the main thread.
 */
export function ScrollFx() {
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const animateCount = (el: HTMLElement) => {
      if (el.dataset.counted) return;
      el.dataset.counted = '1';
      const raw = el.textContent || '';
      const match = raw.match(/\d+/);
      if (!match) return;
      const target = parseInt(match[0], 10);
      const prefix = raw.slice(0, match.index);
      const suffix = raw.slice((match.index || 0) + match[0].length);
      const duration = 1200;
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = `${prefix}${Math.round(target * eased)}${suffix}`;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          el.classList.add('is-visible');
          el.querySelectorAll<HTMLElement>('[data-count]').forEach(animateCount);
          if (el.matches('[data-count]')) animateCount(el);
          io.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    const staggerChildren = (root: ParentNode) => {
      root.querySelectorAll<HTMLElement>('.reveal-stagger').forEach((parent) => {
        Array.from(parent.children).forEach((child, i) => {
          const c = child as HTMLElement;
          c.classList.add('reveal');
          c.style.transitionDelay = `${Math.min(i * 90, 540)}ms`;
        });
      });
    };

    const observeAll = (root: ParentNode) => {
      staggerChildren(root);
      root.querySelectorAll<HTMLElement>('.reveal, [data-count]').forEach((el) => {
        if (!el.classList.contains('is-visible')) io.observe(el);
      });
    };

    observeAll(document);

    // Scroll parallax for elements tagged data-parallax="<speed>"
    let ticking = false;
    const parallax = () => {
      ticking = false;
      document.querySelectorAll<HTMLElement>('[data-parallax]').forEach((el) => {
        const speed = parseFloat(el.dataset.parallax || '0.1');
        const rect = el.getBoundingClientRect();
        const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * -speed;
        el.style.transform = `translateY(${offset.toFixed(1)}px)`;
      });
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(parallax);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    parallax();

    // Client navigations and hydration re-renders mount new nodes later.
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) observeAll(n as Element);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return null;
}
